import { object, number } from 'prop-types';
import { useRef, useEffect, Fragment } from 'react';
import { useSelector } from 'react-redux';

import { selectors } from '@ezugi/bootstrap';
import { AMOUNT_TYPES } from '@ezugi/constants';
import { useFontFaces } from '@ezugi/hooks/src';
import { formatAmount } from '@ezugi/utils';

import {
  drawFan, drawRect, drawStraightText,
  drawText, drawWinAmount, easeInOutQuart,
} from './utils';

const arcUnsupportedLanguages = [ 'hi', 'ta', 'th' ];

const backgroundStyle = {
  height: 300,
};

const counterStyle = {
  ...backgroundStyle,
  position: 'absolute',
  bottom: 10,
};

const { formatSelector, userCurrencySelector, winAmountSelector } = selectors;

const winMessageFont = [
  { fontFamily: 'BuiltTitlingRegular',
    assetUrls: [
      'Built-Titling-Regular.woff2',
      'Built-Titling-Regular.woff',
      'Built-Titling-Regular.ttf',
    ] },
];
const VITE_STATIC_URL = import.meta.env.VITE_STATIC_URL;

function Canvas({ winAmount, duration = 3000 }) {
  const rawWinAmount = useSelector(winAmountSelector);
  const format = useSelector(formatSelector);
  const currency = useSelector(userCurrencySelector);

  const backgroundCanvasRef = useRef();
  const counterCanvasRef = useRef();

  const requestRef = useRef();
  const previousTimeRef = useRef();
  const elapsedTime = useRef(0);

  const { loading } = useFontFaces(`${VITE_STATIC_URL}/fonts`, winMessageFont);

  // Draw the background
  useEffect(() => {
    const canvas = backgroundCanvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!loading) {
      const centerX = canvas.width / 2;
      const centerY = canvas.height - 95;
      const radius = 220;

      ctx.font = '500 90px BuiltTitlingRegular';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (arcUnsupportedLanguages.includes(document.documentElement.lang)) {
        drawRect(ctx, winAmount.label, canvas, centerX, centerY);
        drawStraightText(ctx, winAmount.label, centerX);
      } else {
        drawFan(ctx, canvas);
        drawText(ctx, winAmount.label, centerX, centerY, radius);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }

    return () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
  }, [ loading, winAmount.label ]);

  // Draw the win amount counter
  useEffect(() => {
    const counterCanvas = counterCanvasRef.current;
    const counterCtx = counterCanvas.getContext('2d');

    counterCtx.translate(counterCanvas.width / 2, counterCanvas.height - 75);

    counterCtx.font = 'bold 35pt Roboto';
    counterCtx.textAlign = 'center';
    counterCtx.fillStyle = 'white';
    counterCtx.shadowOffsetX = 3;
    counterCtx.shadowOffsetY = 3;
    counterCtx.shadowBlur = 4;
    counterCtx.shadowColor = 'rgba(0,0,0,0.3)';

    const formattedExactAmount = formatAmount(
      rawWinAmount,
      AMOUNT_TYPES.BET,
      format,
      currency
    );

    const animate = (time) => {
      if (typeof previousTimeRef.current !== 'undefined') {
        const deltaTime = time - previousTimeRef.current;

        elapsedTime.current += deltaTime;

        // Clear the canvas
        counterCtx.clearRect(
          -counterCanvas.width / 2,
          -counterCanvas.height,
          counterCanvas.width,
          counterCanvas.height
        );

        if (elapsedTime.current > duration) {
          // If we have reached the actual won amount, STOP animating
          cancelAnimationFrame(requestRef.current);

          // And draw to the canvas the exact amount, decimals and all
          drawWinAmount(counterCtx, formattedExactAmount);
          return false;
        }

        // Get current progress
        const progress = Math.min(easeInOutQuart(elapsedTime.current / duration), 1);
        // Get new amount
        const newAmount = Math.floor(rawWinAmount * progress);
        // Format increasing and final exact value
        const formattedIncreasingAmount = formatAmount(
          newAmount,
          AMOUNT_TYPES.BET,
          format,
          currency
        );

        // NOTE: Each frame we need to decide if we want to render the growing number, without decimals, or the end
        // result, with two decimals, that will remain displayed until the container goes down.
        if (Math.floor(rawWinAmount) !== newAmount) {
          // Draw to the canvas, render the animated number
          drawWinAmount(counterCtx, formattedIncreasingAmount);
        } else {
          // If we have reached the actual won amount, STOP animating
          cancelAnimationFrame(requestRef.current);

          // And draw to the canvas the exact amount, decimals and all
          drawWinAmount(counterCtx, formattedExactAmount);
          return false;
        }
      }

      previousTimeRef.current = time;
      requestRef.current = requestAnimationFrame(animate);

      return true;
    };

    // Start animation
    requestRef.current = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(requestRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Fragment>
      <canvas
        id="background-layer"
        width={500}
        height={500}
        style={backgroundStyle}
        ref={backgroundCanvasRef}
      />
      <canvas
        id="counter-layer"
        width={500}
        height={500}
        style={counterStyle}
        ref={counterCanvasRef}
      />
    </Fragment>
  );
}

Canvas.propTypes = {
  winAmount: object,
  duration: number,
};

export default Canvas;
